import React, { useState } from 'react';
import { Calendar, CaretLeft, CaretRight } from 'phosphor-react';
import SFooterDrawer, { SFooterDrawerProps } from '../organisms/SFooterDrawer';
import { getTodayInTimezone } from '@/utils/timezone-utils';
import './SDatePicker.css';

export interface SDatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (date: string, dateObj: Date) => void;
  selectedDate?: string;
  className?: string;
  drawerProps?: Partial<SFooterDrawerProps>;
  minDate?: Date; // earliest selectable date; defaults to today if absent
  isDateUnavailable?: (date: Date) => boolean; // e.g. dates with zero booking slots
  timezone?: string; // IANA timezone used to decide which calendar day is "today"; defaults to device timezone
}

const SDatePicker: React.FC<SDatePickerProps> = ({
  isOpen,
  onClose,
  onDateSelect,
  selectedDate,
  className = '',
  drawerProps = {},
  minDate,
  isDateUnavailable,
  timezone,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = getTodayInTimezone(timezone);

  // Effective minimum date — use minDate if it is after today, otherwise fall back to today
  const effectiveMin =
    minDate && !isNaN(minDate.getTime()) && minDate > today ? minDate : today;

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date: Date): (Date | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const formatDateForValue = (date: Date): string => {
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Check if prev button should be disabled based on effectiveMin month
  const isPrevDisabled = (): boolean => {
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const minMonthStart = new Date(effectiveMin.getFullYear(), effectiveMin.getMonth(), 1);
    return currentMonthStart.getTime() <= minMonthStart.getTime();
  };

  const handleDateClick = (date: Date) => {
    if (date < effectiveMin || isDateUnavailable?.(date)) return;
    // Create a new date at local midnight to avoid timezone issues
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const formattedDate = formatDateForValue(localDate);
    onDateSelect(formattedDate, localDate);
    onClose();
  };

  const isDateDisabled = (date: Date): boolean => {
    return date < effectiveMin || !!isDateUnavailable?.(date);
  };

  const isDateSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    const selected = new Date(selectedDate);
    return date.toDateString() === selected.toDateString();
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <SFooterDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Select a Date"
      height="57vh"
      className={`s-date-picker-drawer ${className}`}
      {...drawerProps}
    >
      <div className="s-date-picker">
        {/* Month Navigation */}
        <div className="s-date-picker-header">
          <button
            className={`s-date-picker-nav-btn ${isPrevDisabled() ? 'disabled' : ''}`}
            onClick={handlePrevMonth}
            disabled={isPrevDisabled()}
            aria-label="Previous month"
            type="button"
          >
            <span className={`s-date-picker-nav-icon ${isPrevDisabled() ? 'disabled' : ''}`}>
              <CaretLeft size={20} />
            </span>
          </button>
          <span className="s-date-picker-month">
            {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <button
            className="s-date-picker-nav-btn"
            onClick={handleNextMonth}
            aria-label="Next month"
            type="button"
          >
            <CaretRight size={20} color="#000" />
          </button>
        </div>

        {/* Days of Week Header */}
        <div className="s-date-picker-weekdays">
          {daysOfWeek.map(day => (
            <span key={day} className="s-date-picker-weekday">{day}</span>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="s-date-picker-grid">
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="s-date-picker-day empty" />;
            }

            const disabled = isDateDisabled(date);
            const selected = isDateSelected(date);
            const isToday = date.toDateString() === today.toDateString();

            return (
              <button
                key={date.toISOString()}
                className={`s-date-picker-day ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''} ${isToday ? 'today' : ''}`}
                onClick={() => handleDateClick(date)}
                disabled={disabled}
                type="button"
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    </SFooterDrawer>
  );
};

// Calendar CTA Component
export interface SCalendarCTAProps {
  onClick: () => void;
  className?: string;
}

export const SCalendarCTA: React.FC<SCalendarCTAProps> = ({
  onClick,
  className = '',
}) => {
  return (
    <button
      className={`s-calendar-cta ${className}`}
      onClick={onClick}
      type="button"
    >
      <div className="s-calendar-cta-icon">
        <Calendar size={24} color="#BD9734" weight="regular" />
      </div>
    </button>
  );
};

export default SDatePicker;
